/**
 * System notifications bell — AppBar icon + popover for non-messaging notifications.
 *
 * The messaging unread count is handled by a separate MessagingInboxPopover.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, Check, Inbox } from 'lucide-react';
import { Button } from '../ui/button';
import { Popover } from '../ui/popover';
import {
  useInboxNotifications,
  useInboxUnreadCount,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  notificationsInboxKeys,
  InboxNotification,
} from '../../shared/notifications/useNotificationsInbox';
import { useNotificationHandler } from '../../shared/notifications/useNotificationHandler';
import { applyNotificationDeepLinkTab } from '../../shared/notifications/deepLinkTab';
import { entityUrlContext, getEntityUrl } from '../../shared/utils/entityUrls';
import { localizedNotificationTitle } from '../../shared/notifications/notificationTitle';
import { useResponsive } from '../../shared/hooks/useResponsive';
import { useRelativeDate } from '../../shared/hooks';
import {
  AppBarBadgeButton,
  AppBarPopoverList,
  AppBarPopoverItem,
} from './appBarPopover';

// Notification types handled by the messaging badge, not this bell.
// Mentions and member_added intentionally fall through to the standard bell.
const MESSAGING_TYPES = new Set<string>([
  'message.new',
  'message.updated',
  'message.deleted',
  'conversation.created',
  'conversation.updated',
  'conversation.member_removed',
  'typing.indicator',
]);

interface SystemNotificationsBellProps {
  moduleColor: string;
}

export function notificationHref(n: InboxNotification): string | null {
  if (!n.entity_type || !n.entity_id) return null;
  // Some notification types deep-link to a specific tab (e.g. a new ticket
  // message → Messages, a comment mention → Comments). Centralized in
  // applyNotificationDeepLinkTab so the bell, web toast handler, mobile push and
  // the backend email CTA stay in sync.
  //
  // The cases below are LEGACY bare-noun entity_types persisted in the inbox
  // before the emit sites named real tables; new rows fall through to the Entity
  // URL Registry, which is what every other surface already resolves against.
  switch (n.entity_type) {
    case 'item':
      return `/tasks/item/${n.entity_id}`;
    case 'deal':
      return applyNotificationDeepLinkTab(
        `/pipeline/deals/${n.entity_id}`,
        n.notification_type,
      );
    // 'ticket' = legacy inbox rows persisted before the support catalog landed;
    // 'support.ticket' = the canonical entity_type emitted now.
    case 'ticket':
    case 'support.ticket':
      return applyNotificationDeepLinkTab(
        `/support/tickets/${n.entity_id}`,
        n.notification_type,
      );
    case 'application':
      return `/recruiting/applications/${n.entity_id}`;
    // Sysadmin platform alerts open the Alerts page focused on the alert
    // (AlertsListPage reads ?alert_id=…). entity_id is the alert_id.
    case 'sysadmin_alert':
      return `/sysadmin/alerts?alert_id=${n.entity_id}`;
    case 'shipment':
      return `/shipments/${n.entity_id}?tab=tracking`;
    case 'conversation':
      return `/messaging?conversation=${n.entity_id}`;
    case 'message': {
      const conversationId = n.data?.conversation_id;
      return typeof conversationId === 'string'
        ? `/messaging?conversation=${conversationId}`
        : null;
    }
    default: {
      const url = getEntityUrl({
        entityType: n.entity_type,
        entityId: n.entity_id,
        context: entityUrlContext(n.data),
        // Inbox rows are persisted, so this runs over historical entity_types too.
        // A convention-guessed URL for one of those is worse than no link at all.
        registryOnly: true,
      });
      return url ? applyNotificationDeepLinkTab(url, n.notification_type) : null;
    }
  }
}

export function SystemNotificationsBell({ moduleColor }: SystemNotificationsBellProps) {
  const { t } = useTranslation(['common', 'notifications']);
  const formatRelative = useRelativeDate();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { isMobile } = useResponsive();
  const queryClient = useQueryClient();
  const { data: unreadCount = 0 } = useInboxUnreadCount();
  const { data: notifications = [], isLoading } = useInboxNotifications(30);
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  useNotificationHandler({
    '*': {
      onNotification: (n) => {
        if (MESSAGING_TYPES.has(n.type)) return;
        queryClient.invalidateQueries({ queryKey: notificationsInboxKeys.all });
      },
    },
  });

  if (isMobile && unreadCount === 0) return null;

  const handleClick = (n: InboxNotification) => {
    if (!n.read_at) markRead.mutate(n.id);
    const href = notificationHref(n);
    if (href) {
      // Close the popover so it doesn't linger over the destination page — an
      // in-app navigate() isn't an outside-click, so Radix won't auto-dismiss.
      setOpen(false);
      navigate(href);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <AppBarBadgeButton
        icon={<Bell className="h-4 w-4" />}
        count={unreadCount}
        ariaLabel={t('notifications.bellAriaLabel')}
        tooltip={t('notifications.bellTooltip')}
        moduleColor={moduleColor}
      />
      <AppBarPopoverList
        title={t('notifications.title')}
        headerAction={
          unreadCount > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 text-xs text-muted-foreground"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
            >
              <Check className="h-3.5 w-3.5" />
              {t('notifications.markAllRead')}
            </Button>
          ) : undefined
        }
        isLoading={isLoading}
        isEmpty={notifications.length === 0}
        emptyIcon={<Inbox className="h-8 w-8" />}
        emptyMessage={t('notifications.empty')}
      >
        {notifications.map((n) => (
          <AppBarPopoverItem
            key={n.id}
            onClick={() => handleClick(n)}
            unread={!n.read_at}
            title={localizedNotificationTitle(t, n.notification_type, n.title)}
            subtitle={n.body || undefined}
            meta={formatRelative(new Date(n.created_at))}
          />
        ))}
      </AppBarPopoverList>
    </Popover>
  );
}
